import { type HookAPI } from "antd/es/modal/useModal"

import {
  DO_NOT_CONTACT_REASON_KEY,
  carriesRetiredState
} from "../../Lead/constants/operational-states.ts"
import { LEAD_STATUSES } from "../../Lead/constants/lead-statuses.ts"

interface RestorableLead {
  current_status?: number | null
  /** The list's own `status` is a linked task's workflow label, so only a
   *  numeric one is the lead's stage. */
  status?: number | string | null
  operational_states?: readonly string[] | null
}

/** Either spelling — a row written before the migration still carries the
 *  retired overlay, and restoring it must not skip the consent confirm. */
const asksNotToBeContacted = (lead?: RestorableLead | null) => {
  const stage =
    lead?.current_status ??
    (typeof lead?.status === "number" ? lead.status : undefined)

  return (
    stage === LEAD_STATUSES.DO_NOT_CONTACT ||
    carriesRetiredState(lead?.operational_states, DO_NOT_CONTACT_REASON_KEY)
  )
}

interface ConfirmRestoreArgs {
  modal: HookAPI
  t: (key: string) => string
  lead?: RestorableLead | null
  /** antd drives the OK button's loading state off this return value and keeps
   *  the dialog open until it settles, so a promise must survive the type. */
  onConfirm: () => void | Promise<unknown>
}

/**
 * Shared by the Archive list and the lead's own page so the two entry points
 * cannot drift. Do Not Contact is a stated preference, not a pipeline outcome,
 * so it warns in its own words — the backend refuses neither.
 */
export const confirmLeadRestore = ({
  modal,
  t,
  lead,
  onConfirm
}: ConfirmRestoreArgs) => {
  if (asksNotToBeContacted(lead)) {
    return modal.confirm({
      centered: true,
      title: t("Restore this lead?"),
      content: t(
        "This lead asked not to be contacted. Restoring will move it back into the active pipeline."
      ),
      cancelText: t("Cancel"),
      okText: t("Restore anyway"),
      okButtonProps: { danger: true },
      onOk: onConfirm
    })
  }

  return modal.confirm({
    centered: true,
    title: t("Reactivate this lead?"),
    content: t(
      "The lead returns to its previous stage and stops being inactive."
    ),
    cancelText: t("Cancel"),
    okText: t("Reactivate"),
    onOk: onConfirm
  })
}
